import type { CoreEnv } from '@eventbrite/context-gen';
import { DiscoveryEvent } from '@eventbrite/event-renderer';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import {
    getLocaleOverride,
    gettext,
    setLanguage,
    setup as setupI18n,
} from '@eventbrite/i18n';
import { SiteStructure } from '@eventbrite/site-structure';
import { getLayer, isStatsigInitialized } from '@eventbrite/statsig';
import * as Sentry from '@sentry/react';
import classNames from 'classnames';
import React from 'react';
import { renderToString } from 'react-dom/server';
import { App } from './app';
import { CreatorBanner } from './components/CreatorBanner/CreatorBanner';
import { ActionsPanel } from './components/EventDetails/ActionsPanel/ActionsPanel';
import { ConversionBarContainer } from './components/EventDetails/ConversionBarContainer/ConversionBarContainer';
import { CreatorCollections } from './components/EventDetails/CreatorCollections/CreatorCollections';
import type { EventDescriptionProps } from './components/EventDetails/EventDescription/EventDescription';
import {
    AboutThisEvent,
    OldDescriptionBeforeStructuredContent,
} from './components/EventDetails/EventDescription/EventDescription';
import {
    EventDetails,
    EventDetailsAside,
    EventDetailsMain,
    EventDetailsMainInner,
    EventDetailsWrapper,
} from './components/EventDetails/EventDetails';
import { HeroImage } from './components/EventDetails/HeroImage/HeroImage';
import { MusicEventsInfo } from './components/EventDetails/MusicEventsInfo/MusicEventsInfo';
import { OrganizerInfo } from './components/EventDetails/OrganizerInfo/OrganizerInfo';
import { Performers } from './components/EventDetails/Performers/Performers';
import { RefundPolicy } from './components/EventDetails/RefundPolicy/RefundPolicy';
import { SalesEndedMessage } from './components/EventDetails/SalesEndedMessage/SalesEndedMessage';
import { StartDate } from './components/EventDetails/StartDate/StartDate';
import { Summary } from './components/EventDetails/Summary/Summary';
import { WhenAndWhere } from './components/EventDetails/WhenAndWhere/WhenAndWhere';
import { EventManagerBanner } from './components/EventManagerBanner/EventManagerBanner';
import { ExpiredEventsPage } from './components/ExpiredEvents/ExpiredEventsPage';
import type { FooterLinks } from './components/Footer/Footer';
import { Footer } from './components/Footer/Footer';
import { HighlightsProps } from './components/Highlights';
import type { NotificationBarSsrProps } from './components/NotificationBar/NotificationBarSsr';
import { NotificationBarSsr } from './components/NotificationBar/NotificationBarSsr';
import type { OrganizerPanelLayoutProps } from './components/OrganizerPanel/OrganizerPanelLayout';
import { OrganizerPanelLayout } from './components/OrganizerPanel/OrganizerPanelLayout';
import {
    getShareComponentProps,
    ShareProps,
} from './components/ShareEvent/ShareEventRoot';
import type { ShareInlineProps } from './components/ShareInline/ShareInline';
import { ShareInline } from './components/ShareInline/ShareInline';
import type { Tag } from './components/Tags/Tags';
import { Tags } from './components/Tags/Tags';
import {
    UrgencyCategories,
    UrgencyMessages,
    UrgencySignalsProps,
    UrgencySignalsProvider,
    UrgencySignalsWrapper,
} from './components/UrgencySignals';
import {
    FollowButtonGDPRModal,
    FollowButtonLoginModal,
    FollowButtonProvider,
} from './components/_shared/FollowButton';
import { DescriptiveOrganizerInfo } from './components/_shared/OrganizerInfo/DescriptiveOrganizerInfo';
import { SimplifiedOrganizerInfo } from './components/_shared/OrganizerInfo/SimplifiedOrganizerInfo';
import { LISTING_PAGE_ORGANIZER_PANEL_EXPERIMENT } from './constants';
import { TrackingContext } from './context';
import { getTrackingContextFrom } from './getTrackingContext';
import type {
    AppProps,
    CheckoutWidgetProps,
    Event,
    EventDetailsProps,
    EventHeroProps,
    EventMapScopedProps,
    FollowOrganizerScopedProps,
    LegacyConversionBarProps,
    MainBodyBackgroundScopedProps,
    MusicEventProps,
    Organizer,
    OrganizerInfoExperimentVariantValues,
    Request,
    Settings,
    User,
} from './types';

const FEATURE_FLAGS = {
    launchCreatorCreditsSectionAttendee: true,
};

export interface ServerAppProps {
    checkoutWidget: CheckoutWidgetProps;
    conversionBar: LegacyConversionBarProps;
    user: User;
    event: Event;
    env: CoreEnv;
    request: Request;
    footerLinks: FooterLinks;
    eventDescription: EventDescriptionProps;
    eventDetails: EventDetailsProps;
    organizer: Organizer;
    tags: Array<Tag>;
    settings: Settings;
    shareInline: ShareInlineProps;
    organizerPanelLayout: OrganizerPanelLayoutProps;
    eventMapLayout: EventMapScopedProps;
    eventHero: EventHeroProps & MusicEventProps;
    mainBodyBackground: MainBodyBackgroundScopedProps;
    notificationBar: NotificationBarSsrProps;
    expiredEventsPage: {
        shareComponentProps: ShareProps;
        relatedEvents: DiscoveryEvent[];
    };
    urgencySignals: UrgencySignalsProps;
    highlights: HighlightsProps;
    followOrganizer: FollowOrganizerScopedProps;
    contactOrganizer: { captchaKey: string };
    gaCategory: string;
    gaSettings: any;
    appName: string;
    appVersion: string;
}

export const ServerApp = ({
    checkoutWidget,
    conversionBar,
    user,
    event,
    env,
    request,
    footerLinks,
    eventDescription,
    eventDetails,
    organizer,
    tags,
    settings,
    shareInline,
    organizerPanelLayout,
    eventMapLayout,
    eventHero,
    notificationBar = {
        message: null,
        type: null,
    },
    mainBodyBackground,
    expiredEventsPage,
    urgencySignals,
    highlights,
    followOrganizer,
    contactOrganizer: { captchaKey },
    gaCategory,
    gaSettings,
    appName,
    appVersion,
}: ServerAppProps) => {
    const { hasCroppedLogo } = event;

    const {
        shouldShowSalesEndedMessage,
        organizerSalesEndedMessage,
        salesEndedMessage,
        id: eventId,
    } = event;

    const {
        hasStructuredContent,
        structuredContent,
        summary,
        legacyDescription,
    } = eventDescription;

    const {
        id: organizerId,
        name: organizerName,
        profilePicture: organizerAvatar,
        url: organizerUrl,
        description: organizerDescription,
        orgFacebook: organizerFacebook,
        orgTwitter: organizerTwitter,
        orgWebsite: organizerWebsite,
    } = organizer;

    const { performers } = eventDetails;
    const hasPerformers = performers !== null;

    const organizerPanelExperimentValue = getLayer(
        LISTING_PAGE_ORGANIZER_PANEL_EXPERIMENT.layer,
        LISTING_PAGE_ORGANIZER_PANEL_EXPERIMENT.param,
        LISTING_PAGE_ORGANIZER_PANEL_EXPERIMENT.defaultValue,
    );

    const isOldOrganizerPanelControlVariant =
        organizerPanelExperimentValue ===
        LISTING_PAGE_ORGANIZER_PANEL_EXPERIMENT.defaultValue;

    if (HAS_WINDOW && !isStatsigInitialized()) {
        return null;
    }

    const SimplifiedOrganizerInfoComponent = () => {
        return isOldOrganizerPanelControlVariant ? (
            <OrganizerInfo
                profileLink={organizer.url}
                nameTruncated={organizer.nameTruncated}
                organizerId={organizer.id}
                gaCategory={gaCategory}
            />
        ) : (
            <SimplifiedOrganizerInfo
                organizerName={organizerName}
                organizerAvatar={organizerAvatar}
                organizerUrl={organizerUrl}
                organizerId={organizerId}
            />
        );
    };

    const MainContent = () => {
        return (
            <>
                <div
                    className={`js-hidden-when-expired event-listing ${
                        hasCroppedLogo ? 'event-listing--has-image' : ''
                    } ${shouldHideMainBody ? 'is-hidden' : ''}`}
                    role="article"
                >
                    {notificationBar.message && (
                        <NotificationBarSsr {...notificationBar} />
                    )}
                    {user.canManageEvent &&
                        !settings.featureFlags
                            ?.launchMobilePreviewInEventCreationFlow && (
                            <CreatorBanner
                                eventId={eventId}
                                settings={settings}
                            />
                        )}
                    <EventDetails>
                        <HeroImage
                            background={mainBodyBackground.croppedLogoUrl50}
                            hasCroppedLogo={event.hasCroppedLogo}
                            croppedLogoUrl480={eventHero.croppedLogoUrl480}
                            croppedLogoUrl600={eventHero.croppedLogoUrl600}
                            croppedLogoUrl800={eventHero.croppedLogoUrl800}
                            croppedLogoUrl940={eventHero.croppedLogoUrl940}
                            eventName={event.name}
                        />
                        <UrgencySignalsProvider urgencySignals={urgencySignals}>
                            <EventDetailsWrapper>
                                <ActionsPanel
                                    isShareable={conversionBar.isShareable}
                                    isEnded={conversionBar.isEnded}
                                />
                                <EventDetailsMain>
                                    <EventDetailsMainInner>
                                        <SalesEndedMessage
                                            shouldShowSalesEndedMessage={
                                                shouldShowSalesEndedMessage
                                            }
                                            organizerSalesEndedMessage={
                                                organizerSalesEndedMessage
                                            }
                                            salesEndedMessage={
                                                salesEndedMessage
                                            }
                                        />
                                        <UrgencySignalsWrapper>
                                            <div>
                                                <div className="urgency-signals">
                                                    <div className="eds-show-down-mn">
                                                        <UrgencyMessages />
                                                    </div>
                                                    <UrgencyCategories />
                                                </div>
                                            </div>
                                        </UrgencySignalsWrapper>
                                        <StartDate
                                            startDateTime={event.startDateTime}
                                            startMonthAbbr={
                                                event.startMonthAbbr
                                            }
                                            startDay={event.startDay}
                                        />
                                        <meta content={event.name} />
                                        <h1 className="event-title">
                                            {event.name}
                                        </h1>
                                        {eventHero.hasPresenterOrAgeRestriction ||
                                        event.hasPresenterOrAgeRestriction ? (
                                            <MusicEventsInfo
                                                ageRestriction={
                                                    eventHero.ageRestriction ||
                                                    event.ageRestriction
                                                }
                                                presenter={
                                                    eventHero.presenter ||
                                                    event.presenter
                                                }
                                            />
                                        ) : null}
                                        <CreatorCollections eventId={eventId} />
                                        <Summary summary={summary} />
                                        {organizer.displayOrganizationName && (
                                            <SimplifiedOrganizerInfoComponent />
                                        )}
                                    </EventDetailsMainInner>
                                    <WhenAndWhere
                                        dates={{
                                            ...eventDetails.dates,
                                            hideEndDate: event.hideEndDate,
                                            hideStartDate: event.hideStartDate,
                                            childEventsDropdown: {
                                                childEvents:
                                                    event.childEvents || [],
                                                eventId,
                                                isOnlineEvent:
                                                    event.isOnlineEvent,
                                            },
                                            shouldDisplayCrawlableSeriesChildrenLinks:
                                                eventDetails.dates
                                                    .shouldDisplayCrawlableSeriesChildrenLinks,
                                            childEvents: event.childEvents,
                                        }}
                                        location={{
                                            ...eventDetails.location,
                                            isOnlineEvent: event.isOnlineEvent,
                                            onlineEventUrl:
                                                eventDetails.onlineEvent?.url,
                                        }}
                                        map={{
                                            location: eventMapLayout.location,
                                            gMapsApiKey:
                                                eventMapLayout.gMapsApiKey,
                                            shouldDisplayMap:
                                                eventMapLayout.shouldDisplayMap,
                                        }}
                                        isSeriesParent={event.isSeriesParent}
                                    />
                                    <EventDetailsMainInner>
                                        {hasPerformers && (
                                            <Performers
                                                headliners={
                                                    eventDetails.performers
                                                        .headliners
                                                }
                                                supporters={
                                                    eventDetails.performers
                                                        .supporters
                                                }
                                            />
                                        )}
                                        <RefundPolicy
                                            isEnableRetainFeeActive={
                                                eventDetails.refund
                                                    .isEnableRetainFeeActive
                                            }
                                            isPaidEvent={
                                                eventDetails.refund.isPaidEvent
                                            }
                                            isRefundRequestAllowed={
                                                eventDetails.refund
                                                    .isRefundRequestAllowed
                                            }
                                            refundPolicyDescription={
                                                eventDetails.refund
                                                    .refundPolicyDescription
                                            }
                                            refundPolicyIsNoRefunds={
                                                eventDetails.refund
                                                    .refundPolicyIsNoRefunds
                                            }
                                        />
                                        {hasStructuredContent ||
                                        !legacyDescription ? (
                                            <AboutThisEvent
                                                hasStructuredContent={
                                                    hasStructuredContent
                                                }
                                                admat={structuredContent.admat}
                                                modules={
                                                    structuredContent.modules
                                                }
                                                highlights={highlights}
                                            />
                                        ) : (
                                            <OldDescriptionBeforeStructuredContent
                                                legacyDescription={
                                                    legacyDescription
                                                }
                                                highlights={highlights}
                                            />
                                        )}
                                        <Tags tags={tags} />
                                        <ShareInline {...shareInline} />
                                        {isOldOrganizerPanelControlVariant ? (
                                            <OrganizerPanelLayout
                                                {...organizerPanelLayout}
                                            />
                                        ) : (
                                            <>
                                                <DescriptiveOrganizerInfo
                                                    captchaKey={captchaKey}
                                                    organizerAvatar={
                                                        organizerAvatar
                                                    }
                                                    organizerDescription={
                                                        organizerDescription
                                                    }
                                                    organizerId={organizerId}
                                                    organizerName={
                                                        organizerName
                                                    }
                                                    organizerUrl={organizerUrl}
                                                    organizerFacebook={
                                                        organizerFacebook
                                                    }
                                                    organizerTwitter={
                                                        organizerTwitter
                                                    }
                                                    organizerWebsite={
                                                        organizerWebsite
                                                    }
                                                    isAuthenticated={
                                                        user.isAuthenticated
                                                    }
                                                    eventId={eventId}
                                                    eventName={event.name}
                                                    gaCategory={gaCategory}
                                                />
                                                <section className="listing-organizer eds-l-pad-bot-5">
                                                    <hr className="eds-l-mar-top-2" />
                                                    {organizerPanelLayout.shouldShowMoreEventsFromThisOrganizer && (
                                                        <div id="listings-root__more-events-from-this-organizer"></div>
                                                    )}
                                                    <div id="listings-root__collections-related-to-this-event"></div>
                                                </section>
                                            </>
                                        )}
                                    </EventDetailsMainInner>
                                </EventDetailsMain>
                                <EventDetailsAside>
                                    <UrgencySignalsWrapper>
                                        <div className="eds-show-up-md eds-text--right eds-l-mar-bot-4">
                                            <UrgencyMessages />
                                        </div>
                                    </UrgencySignalsWrapper>
                                    <ConversionBarContainer
                                        {...{
                                            ...conversionBar,
                                            ...checkoutWidget,
                                            externalTickets: {
                                                url: conversionBar.externalTicketsUrl,
                                                text: conversionBar.externalTicketsButtonText,
                                                available:
                                                    conversionBar.externalTicketsAreAvailable,
                                                shouldShowExternalTicketingProvider:
                                                    eventHero.shouldShowExternalTicketingProvider ||
                                                    conversionBar.shouldShowExternalTicketingProvider,
                                                externalTicketingProviderLabelText:
                                                    eventHero.externalTicketingProviderLabelText ||
                                                    conversionBar.externalTicketingProviderLabelText,
                                            },
                                            eventId,
                                            childEvents: event.childEvents,
                                            isSeriesParent:
                                                event.isSeriesParent,
                                            isExpiredEvent:
                                                event.isExpiredEvent,
                                            isPreview: event.isPreview,
                                            eventName: event.name,
                                        }}
                                    />
                                </EventDetailsAside>
                            </EventDetailsWrapper>
                        </UrgencySignalsProvider>
                    </EventDetails>

                    {shouldShowRelatedEventsLive && (
                        <div id="listings-root__related-events"></div>
                    )}
                    <EventManagerBanner
                        eventId={eventId}
                        canManage={user.canManageEvent || false}
                    />
                </div>

                {shouldHideMainBody && (
                    <TrackingContext.Provider
                        value={getTrackingContextFrom({
                            user,
                            event,
                            settings,
                            env,
                            request,
                        })}
                    >
                        <ExpiredEventsPage
                            relatedEvents={expiredEventsPage.relatedEvents}
                            eventName={event.name}
                            organizer={{
                                name: organizerName,
                                profilePicture: organizerAvatar,
                                url: organizerUrl,
                                id: organizerId,
                            }}
                            isAuthenticated={user.isAuthenticated}
                            shareComponentProps={
                                expiredEventsPage.shareComponentProps
                            }
                            isOldOrganizerPanelControlVariant={
                                isOldOrganizerPanelControlVariant
                            }
                        />
                    </TrackingContext.Provider>
                )}
            </>
        );
    };

    const shouldHideMainBody = !event.isPreview && event.isExpiredEvent;

    const shouldShowRelatedEventsLive = event.isLive;

    const cleanEsiCachePaths = (env: CoreEnv) => {
        const esiPath = user.isAuthenticated
            ? /%2Fesi_nocache/g
            : /%2Fesi_cache/g;
        const removeEsiPath = (string: string): string =>
            string ? string.replace(esiPath, '') : '';

        env.loginUrl = removeEsiPath(env.loginUrl);
        env.logoutUrl = removeEsiPath(env.logoutUrl);
        env.signinUrl = removeEsiPath(env.signinUrl);
        env.signupUrl = removeEsiPath(env.signupUrl);
    };

    cleanEsiCachePaths(env);

    const siteWrapperClasses = classNames({
        'conversion-bar-placeholder': !event.isExpiredEvent,
    });

    return (
        <Sentry.ErrorBoundary
            fallback={<>{gettext('Error on Listings SSR')}</>}
        >
            <div className={siteWrapperClasses}>
                <SiteStructure
                    env={env}
                    request={request}
                    user={user}
                    preview={event.isPreview}
                    featureFlags={FEATURE_FLAGS}
                    fullScreenMain={true}
                    footer={
                        <Footer {...footerLinks} serverUrl={env.serverUrl} />
                    }
                    removeOverflowSettings={true}
                    focusDrawerOptions={{ content: '', alwaysInclude: false }}
                >
                    {isOldOrganizerPanelControlVariant ? (
                        <MainContent />
                    ) : (
                        <FollowButtonProvider
                            organizerPanelVariantVersion={
                                organizerPanelExperimentValue as OrganizerInfoExperimentVariantValues
                            }
                            organizerId={organizerId}
                            gaSettings={{
                                ...gaSettings,
                                appName,
                                appVersion,
                            }}
                            userId={user.id}
                            isGDPRCountry={followOrganizer.isGDPRCountry}
                        >
                            <MainContent />
                            <FollowButtonGDPRModal />
                            <FollowButtonLoginModal
                                organizerName={organizerName}
                                organizerAvatar={organizerAvatar}
                            />
                        </FollowButtonProvider>
                    )}
                </SiteStructure>
            </div>
        </Sentry.ErrorBoundary>
    );
};

export const render = (props: AppProps) => {
    const locale = getLocaleOverride() || props.env.localeInfo.locale;
    setupI18n(require.context('./i18n/translations', true, /\.json$/));
    setLanguage(locale);

    return renderToString(
        <ServerApp
            user={props.user}
            event={{ ...props.event }}
            request={props.request}
            env={props.env}
            footerLinks={props.footerLinks}
            eventDescription={{ ...props.components.eventDescription }}
            eventDetails={{ ...props.components.eventDetails }}
            tags={props.components.tags}
            shareInline={props.components.shareInline}
            conversionBar={props.components.conversionBar}
            checkoutWidget={props.components.checkoutWidget}
            settings={props.settings}
            organizerPanelLayout={props.components.organizerPanel}
            eventMapLayout={props.components.eventMap}
            eventHero={props.components.eventHero}
            organizer={props.organizer}
            mainBodyBackground={props.components.mainBodyBackground}
            notificationBar={props.components.notificationBar}
            expiredEventsPage={{
                ...props.components.relatedEventsExpiredPage,
                shareComponentProps: getShareComponentProps(props),
            }}
            urgencySignals={props.urgencySignals}
            highlights={props.highlights}
            gaCategory={props.gaSettings.category}
            gaSettings={props.gaSettings}
            appVersion={props.app_version}
            appName={props.app_name}
            followOrganizer={props.components.followOrganizer}
            contactOrganizer={props.components.contactOrganizer}
        />,
    );
};

export default App;
